import axios, { AxiosInstance } from 'axios';
import { getRuntimeEnvironment } from '@trappedmc/docker-frontend-runtime-env';
import { RootStore } from '../../state/RootStore';
import { StoreStore } from './StoreStore';
import { CartController } from '../cart/CartController';
import PACKAGE_BANNERS from '../../../package-banners.json';

const {
  REACT_APP_SERVER_API,
  REACT_APP_DISCORD_SERVER_ID = '983800084212908072',
  REACT_APP_DISCORD_INVITE = 'https://discord.gg/trappedmc',
  REACT_APP_MINECRAFT_SERVER_ADDRESS = 'mc.trappedmc.com',
  REACT_APP_MINECRAFT_SERVER_PORT = '25565',
  REACT_APP_MINECRAFT_IP = 'mc.trappedmc.com'
} = getRuntimeEnvironment();

export { REACT_APP_MINECRAFT_IP, REACT_APP_DISCORD_INVITE };

export class StoreController {
  private readonly store: StoreStore;
  private readonly api: AxiosInstance;
  private readonly cartController: CartController;

  constructor(private readonly rootStore: RootStore) {
    this.store = rootStore.storeStore;
    this.cartController = new CartController(rootStore);
    this.api = axios.create({
      baseURL: REACT_APP_SERVER_API
    });
  }

  public async load(): Promise<void> {
    await this.restoreSession();
    this.loadProductBanners();
    await Promise.all([
      this.loadCollections(),
      this.loadDiscordStats(),
      this.loadMinecraftStats()
    ]);
  }

  public async restoreSession(): Promise<void> {
    // fetchCart no-ops when there's no stored cart and silently drops a stale one,
    // so no extra guard/cleanup is needed here.
    await this.cartController.fetchCart();
  }

  public async loadCollections(): Promise<void> {
    this.store.setLoading(true);
    try {
      const response = await this.api.get('/merch/collections');

      if (!response.data.success || !response.data.collections) {
        throw new Error(response.data.error || 'Failed to load collections');
      }

      this.store.updateCollections(response.data.collections);

      const firstCollection = response.data.collections[0];
      if (firstCollection?.products && firstCollection.products.length > 0) {
        const firstProduct = firstCollection.products[0];
        if (firstProduct?.priceRange?.minVariantPrice?.currencyCode) {
          const currency = firstProduct.priceRange.minVariantPrice.currencyCode;
          this.rootStore.cartStore.currency = currency;
          localStorage.setItem('merch_currency', currency);
        }
      }
    } catch (error) {
      console.error(error);
      this.rootStore.alertStore.showAlert('Failed to load collections', 'error');
    } finally {
      this.store.setLoading(false);
    }
  }

  public async loadDiscordStats(): Promise<void> {
    this.store.setDiscordLoading(true);
    try {
      const response = await axios.get(
        `https://discord.com/api/guilds/${REACT_APP_DISCORD_SERVER_ID}/widget.json`
      );
      this.store.setDiscordOnline(response.data.presence_count || 0);
    } catch (error) {
      console.error('Failed to load Discord stats:', error);
      this.store.setDiscordOnline(0);
    } finally {
      this.store.setDiscordLoading(false);
    }
  }

  public async loadMinecraftStats(): Promise<void> {
    this.store.setMinecraftLoading(true);
    try {
      const response = await axios.get(
        `https://api.minetools.eu/ping/${REACT_APP_MINECRAFT_SERVER_ADDRESS}/${REACT_APP_MINECRAFT_SERVER_PORT}`
      );
      this.store.setPlayersOnline(response.data.players?.online || 0);
    } catch (error) {
      console.error('Failed to load Minecraft stats:', error);
      this.store.setPlayersOnline(0);
    } finally {
      this.store.setMinecraftLoading(false);
    }
  }

  private loadProductBanners(): void {
    try {
      const bannersMap = new Map<string, { text: string; color: string }>();
      PACKAGE_BANNERS.banners.forEach((banner: any) => {
        bannersMap.set(banner.packageId.toString(), {
          text: banner.text,
          color: banner.color
        });
      });
      this.store.setBanners(bannersMap);
    } catch (error) {
      console.error('Failed to load product banners:', error);
    }
  }
}
